import PropTypes from "prop-types";
import { createContext, useEffect, useReducer, useCallback, useMemo, useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  GoogleAuthProvider,
  GithubAuthProvider,
  TwitterAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  deleteUser,
} from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getFirestore, collection, doc, runTransaction } from "firebase/firestore";
// config
import { FIREBASE_API } from "../config-global";
import { useUser } from "src/hooks/useUser";
import { getUser } from "src/service/users";
import { accessTypes, TEMPORAL_LOGIN_DISABLE, usersTypes } from "src/utils/constants";
import { REGIONS_COLLECTION, USER_REGIONS_COLLECTION } from "src/constants/collectionsIds";
import { USER_COLLECTION } from "src/utils/collectionsIds";
import { setSessionToken } from "./utils";
import localStorageAvailable from "src/utils/localStorageAvailable";
import { isAdmin, isInstructor, isStudent } from "src/utils/utils";
import { genders } from "src/constants/constants";
// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

const firebaseApp = initializeApp(FIREBASE_API);

const AUTH = getAuth(firebaseApp);

const DB = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

const GOOGLE_PROVIDER = new GoogleAuthProvider();

const GITHUB_PROVIDER = new GithubAuthProvider();

const TWITTER_PROVIDER = new TwitterAuthProvider();
export { AUTH, DB, storage };

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [userId, setUserId] = useState(null);
  const { user, loading } = useUser(userId);
  const [userAuth, setUserAuth] = useState(null);
  const [, setAuthCurrentToken] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem("accessToken") : "";

      if (accessToken) {
        setSessionToken(accessToken);
        setAuthCurrentToken(accessToken);
      }
    } catch (error) {
      console.error(error);
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const removeUser = useCallback(async () => {
    dispatch({
      type: "INITIAL",
      payload: {
        isAuthenticated: false,
        user: null,
      },
    });
  }, [dispatch]);
  const addUser = useCallback(
    async (user, userAuth) => {
      dispatch({
        type: "INITIAL",
        payload: {
          isAuthenticated: true,
          user: {
            ...userAuth,
            ...user,
            role: user.userType || "student",
          },
        },
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (TEMPORAL_LOGIN_DISABLE) return;
    const off = onAuthStateChanged(AUTH, async (user) => {
      if (!user) return removeUser();
      setUserAuth(user);
      setUserId(user.uid);
      const userD = await getUser(user.uid);
      addUser(userD, user);
    });
    return () => off();
  }, [removeUser, addUser]);

  useEffect(() => {
    if (loading) return;
    if (!user || !userAuth) {
      removeUser();
      return;
    }
    addUser(user, userAuth);
  }, [removeUser, user, loading, userAuth, addUser]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const res = await signInWithEmailAndPassword(AUTH, email, password);
    console.log(res.user.uid);
    setUserId(res.user.uid);
    //setAuthCurrentToken(res.user.accessToken)
  }, []);

  const loginWithGoogle = useCallback(() => {
    signInWithPopup(AUTH, GOOGLE_PROVIDER);
  }, []);

  const loginWithGithub = useCallback(() => {
    signInWithPopup(AUTH, GITHUB_PROVIDER);
  }, []);

  const loginWithTwitter = useCallback(() => {
    signInWithPopup(AUTH, TWITTER_PROVIDER);
  }, []);
  const addUserDoc = async (user, uid) => {
    const userRef = doc(collection(DB, USER_COLLECTION), uid);
    let { userType, firstName, lastName, regionId, accessType, email } = user;
    const displayName = `${firstName || ""} ${lastName || ""}`.trim().replace(/ {2}/g, " ");
    await runTransaction(DB, async (transaction) => {
      if (Array.isArray(regionId) && regionId?.length) {
        regionId = await Promise.all(
          regionId.map(async (region) => {
            const regionRef = doc(collection(DB, REGIONS_COLLECTION), region);
            const regionData = await transaction.get(regionRef);
            if (!regionData.exists()) return null;
            const { usersCount, instructorCount, studentCount } = regionData.data();
            const data = {
              usersCount: Number(usersCount || 0) + 1,
              instructorCount: Number(instructorCount || 0),
              studentCount: Number(studentCount || 0),
            };
            if (userType === usersTypes.instructor) data.instructorCount++;
            if (userType === usersTypes.student) data.studentCount++;
            transaction.update(regionRef, data);
            return region;
          }),
        );
        transaction.set(doc(DB, USER_REGIONS_COLLECTION, uid), {
          userId: uid, //reference to user
          userName: displayName || "",
          userType: userType, //enum
          regionId,
        });
      }
      if (!accessType) {
        accessType = [accessTypes.driving];
      }
      if (typeof accessType === "string") {
        accessType = [accessType];
      }
      if (!Array.isArray(accessType)) {
        accessType = [accessTypes.driving];
      }
      accessType = accessType.filter((aT) => accessTypes[aT]);
      transaction.set(userRef, {
        uid,
        displayName,
        userType: user?.userType || usersTypes.student,
        regionId: regionId || [],
        phone: user?.phone || null,
        address: user?.address || null,
        accessType,
        email,
        archived: false,
        drivingInstructor: user?.drivingInstructor || null,
        createdAt: Date.now(),
        billingCredits: 0,
        available: [],
        daysAvailable: [],
        message: "",
        birthDate: new Date(user?.birthDate).toISOString(),
      });
    });
  };

  const editUserDoc = useCallback(async (user, uid) => {
    let { userType, firstName, lastName, regionId, accessType } = user;
    const displayName = `${firstName || ""} ${lastName || ""}`.trim().replace(/ {2}/g, " ");
    if (typeof regionId === "string") regionId = [regionId];
    if (!uid) throw new Error("user uid is required");
    await runTransaction(DB, async (transaction) => {
      const userRef = doc(collection(DB, USER_COLLECTION), uid);
      const prevUser = await transaction.get(userRef);
      const prevUserData = prevUser.data();
      const prevRegionId = prevUserData?.regionId || [];
      if (!Array.isArray(regionId) && regionId?.length) regionId = [];
      const regions = {};
      regionId = await Promise.all(
        regionId.map(async (region) => {
          const regionRef = doc(collection(DB, REGIONS_COLLECTION), region);
          const regionData = await transaction.get(regionRef);
          if (!regionData.exists()) return null;

          regions[region] = regionData.data();
          return region;
        }),
      );
      const removeRegions = prevRegionId?.filter((region) => !regionId.includes(region));
      const addRegions = regionId.filter((region) => !prevRegionId?.includes(region));

      addRegions.map(async (region) => {
        const { usersCount, instructorCount, studentCount } = regions[region];
        const regionRef = doc(collection(DB, REGIONS_COLLECTION), region);
        const data = {
          usersCount: Number(usersCount || 0) + 1,
          instructorCount: Number(instructorCount || 0),
          studentCount: Number(studentCount || 0),
        };
        if (userType === usersTypes.instructor) data.instructorCount++;
        if (userType === usersTypes.student) data.studentCount++;
        regions[region] = { ...regions[region], ...data };
        transaction.update(regionRef, data);
      });
      removeRegions.map(async (region) => {
        const { usersCount, instructorCount, studentCount } = regions[region];
        const regionRef = doc(collection(DB, REGIONS_COLLECTION), region);
        const data = {
          usersCount: Number(usersCount || 0) - 1,
          instructorCount: Number(instructorCount || 0),
          studentCount: Number(studentCount || 0),
        };
        if (userType === usersTypes.instructor) data.instructorCount--;
        if (userType === usersTypes.student) data.studentCount--;
        regions[region] = { ...regions[region], ...data };
        transaction.update(regionRef, data);
      });
      transaction.set(doc(DB, USER_REGIONS_COLLECTION, uid), {
        userId: uid, //reference to user
        userName: displayName || "",
        userType: userType, //enum
        regionId,
      });

      if (!accessType) {
        accessType = [accessTypes.driving];
      }
      if (typeof accessType === "string") {
        accessType = [accessType];
      }
      if (!Array.isArray(accessType)) {
        accessType = [accessTypes.driving];
      }
      accessType = accessType.filter((aT) => accessTypes[aT]);
      const gender = genders.find((gen) => gen.value === user?.gender)?.value || "other";
      transaction.update(userRef, {
        uid,
        displayName,
        firstName: firstName || "",
        lastName: lastName || "",
        userType: user?.userType || usersTypes.student,
        regionId: regionId || [],
        phone: user?.phone || null,
        address: user?.address || null,
        zipCode: user?.zipCode || null,
        pinPid: user?.pinPid || null,
        accessType,
        email: prevUserData?.email,
        archived: Boolean(prevUserData?.archived),
        drivingInstructor: user?.drivingInstructor || prevUserData?.drivingInstructor || null,
        createdAt: prevUserData?.createdAt || Date.now(),
        credits: prevUserData?.credits || [{ courseId: "drivingLessons", credits: 0 }],
        available: prevUserData?.available || [],
        daysAvailable: prevUserData?.daysAvailable || [],
        message: prevUserData?.message || "",
        birthDate: new Date(user?.birthDate).toISOString(),
        gender,
      });
    });
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, data) => {
    let user = null;
    try {
      const res = await createUserWithEmailAndPassword(AUTH, email, password);
      user = res.user;
      await addUserDoc(data, user.uid);
    } catch (error) {
      console.error({ error });
      if (user) deleteUser(user);
    }
  }, []);

  // const createPaymentTwint = async ({
  //   userId,
  //   type,
  //   description,
  //   actionType,
  //   category,
  //   paymentDate,
  //   amount,
  //   paymentMethod,
  //   units,
  //   regionId,
  // }) => {
  //   try {
  //     const resp = await TestApi.post("/payments/createPayment", {
  //       userId: userId,
  //       type: type,
  //       description: description,
  //       actionType: actionType,
  //       category: category,
  //       paymentDate: paymentDate,
  //       amount: amount,
  //       paymentMethod: paymentMethod,
  //       units: units,
  //       regionId: regionId,
  //     });
  //     console.log(resp);
  //   } catch (error) {
  //     console.log(error.response.data);
  //   }
  // };

  // LOGOUT
  const logout = useCallback(async () => {
    await signOut(AUTH);
    setUserId(null);
  }, []);

  const role = useMemo(() => {
    return {
      isAdmin: isAdmin(state.user),
      isStudent: isStudent(state.user),
      isInstructor: isInstructor(state.user),
    };
  }, [state.user]);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: "firebase",
      login,
      loginWithGoogle,
      loginWithGithub,
      loginWithTwitter,
      register,
      addUserDoc,
      editUserDoc,
      logout,
      transactionId,
      setTransactionId,
      ...role,
    }),
    [
      state.isInitialized,
      state.isAuthenticated,
      state.user,
      login,
      loginWithGoogle,
      loginWithGithub,
      loginWithTwitter,
      register,
      editUserDoc,
      logout,
      transactionId,
      role,
    ],
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
